import {bool, func, number, object} from 'prop-types';
import {useCallback, useEffect, useState} from 'react';

import {BODYWEIGHT_MAX, BODYWEIGHT_MIN} from '../../../../lib/training.mjs';
import {UNIT_DISPLAY} from '../constants.js';
import useLocalStore from '../utils/useLocalStore.js';
import Dialog from './ui/Dialog.jsx';
import Message from './ui/Message.jsx';
import TextInput from './ui/TextInput.jsx';

export default function MesoDayReadynessDialog({
  onSave,
  loading,
  meso,
  bodyweight,
  onClose,
  hasCompletedBodyweightSets,
}) {
  const [weight, setWeight] = useState('');
  const [focused, setFocused] = useState('');
  const [found, setFound] = useState(false);

  const readynessDialogOpen = useLocalStore(st => st.readynessDialogOpen);

  const handleClose = useCallback(() => {
    setWeight('');
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!readynessDialogOpen && weight) {
      setWeight('');
      setFound(false);
    }
  }, [readynessDialogOpen, weight]);

  const buttonDisabled =
    loading || weight < BODYWEIGHT_MIN[meso.unit] || weight > BODYWEIGHT_MAX[meso.unit];

  // Find best initial value for weight input.
  useEffect(() => {
    if (readynessDialogOpen && !weight) {
      if (!bodyweight) {
        if (!found) {
          // if we haven't already found something, look backwards in the meso for a previous bodyweight
          const mesoDays = meso.weeks.flatMap(week => week.days);

          for (let i = mesoDays.length - 1; i >= 0; --i) {
            if (mesoDays[i].bodyweight) {
              setWeight(mesoDays[i].bodyweight);
              setFound(true);
              return;
            }
          }
        }
      } else if (!focused) {
        // start with bodyweight
        setWeight(bodyweight);
      }
    }
  }, [bodyweight, focused, found, meso.weeks, readynessDialogOpen, weight]);

  return (
    <Dialog
      isOpen={readynessDialogOpen}
      onClose={onClose}
      size="lg"
      title={bodyweight ? 'Workout bodyweight' : 'Enter your bodyweight to begin'}
    >
      <div className="space-y-4 text-sm">
        {bodyweight && hasCompletedBodyweightSets ? (
          <Message variant="warning">
            Your updated bodyweight will be applied to already performed sets
          </Message>
        ) : (
          <div>
            <p>This workout contains an exercise that requires your bodyweight.</p>
            {found && (
              <p className="text-base-content">
                * We prefilled your bodyweight based on a previous day
              </p>
            )}
            <p>You can always update your bodyweight from the day menu.</p>
          </div>
        )}
        <div className="max-w-[125px]">
          <TextInput
            label="Bodyweight"
            type="number"
            value={weight}
            onChange={setWeight}
            onBlur={() => setFocused(false)}
            onFocus={() => setFocused(true)}
            pattern="[0–9]*"
            min="50" // do these work in any real way?
            max="400"
            trailingAddOn={
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-base-content">
                {UNIT_DISPLAY.plural[meso.unit]}
              </div>
            }
          />
        </div>

        {/* turn into an alert style */}
      </div>

      <div className="mt-4 flex justify-between">
        <div className="relative mt-2 flex items-start"></div>

        <div className="flex items-center space-x-3">
          <button className="btn btn-ghost" onClick={handleClose}>
            Cancel
          </button>
          <button
            disabled={buttonDisabled}
            className="btn btn-accent"
            onClick={() => onSave(weight, meso.unit)}
          >
            {loading && <span className="loading"></span>}
            {bodyweight ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

MesoDayReadynessDialog.propTypes = {
  bodyweight: number,
  loading: bool.isRequired,
  meso: object.isRequired,
  onClose: func.isRequired,
  onSave: func.isRequired,
  hasCompletedBodyweightSets: bool.isRequired,
};
